// ===========================================
// The children on an enquiry (enquiries plan §7, Phase 2)
// ===========================================
// Shared by the add form and the details dialog. An enquiry can list several children, or none
// (an adult learner, a phone call that named nobody). A row keeps its server `id` so the PATCH
// updates that child instead of replacing it (the server keeps a child's link to a student). A
// child already enrolled as a student cannot be removed: the server refuses it, so the row
// offers no Remove. The rows and their payload are in `childRows.ts`.

import { Box, Button, Grid, IconButton, TextField, Tooltip, Typography, Autocomplete } from '@mui/material';
import { Add as AddIcon, DeleteOutline as RemoveIcon } from '@mui/icons-material';
import { usePermissions } from '../../../hooks/usePermissions';
import { useInstruments } from '../../../hooks/useAdmin';
import { CHILDREN_MAX, ageError, newChildRow, type ChildRow } from './childRows';

interface ChildrenEditorProps {
  rows: ChildRow[];
  onChange: (rows: ChildRow[]) => void;
  disabled?: boolean;
}

export default function ChildrenEditor({ rows, onChange, disabled }: ChildrenEditorProps) {
  const { can } = usePermissions();
  const canListInstruments = can('class_config', 'read');
  const { data: instruments } = useInstruments({ enabled: canListInstruments });
  const instrumentNames = Array.isArray(instruments) ? instruments.map((i) => i.name) : [];

  const update = (key: string, patch: Partial<ChildRow>) => onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const remove = (key: string) => onChange(rows.filter((r) => r.key !== key));

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Children
      </Typography>
      {rows.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          None listed. That is fine for an adult learner, or when the family has not said yet.
        </Typography>
      )}
      {rows.map((row, i) => {
        const err = ageError(row.age);
        return (
          <Grid container spacing={1} key={row.key} sx={{ mb: 1 }} alignItems="flex-start" data-testid="child-row">
            <Grid item xs={6} sm={3}>
              <TextField
                label="First name"
                size="small"
                fullWidth
                value={row.firstName}
                onChange={(e) => update(row.key, { firstName: e.target.value })}
                inputProps={{ maxLength: 100, 'aria-label': `Child ${i + 1} first name` }}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Last name"
                size="small"
                fullWidth
                value={row.lastName}
                onChange={(e) => update(row.key, { lastName: e.target.value })}
                inputProps={{ maxLength: 100, 'aria-label': `Child ${i + 1} last name` }}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField
                label="Age"
                size="small"
                fullWidth
                value={row.age}
                onChange={(e) => update(row.key, { age: e.target.value })}
                inputProps={{ inputMode: 'numeric', maxLength: 3, 'aria-label': `Child ${i + 1} age` }}
                error={!!err}
                helperText={err ?? undefined}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Autocomplete
                freeSolo
                options={instrumentNames}
                inputValue={row.interest}
                onInputChange={(_, value) => update(row.key, { interest: value.slice(0, 100) })}
                disabled={disabled}
                renderInput={(params) => (
                  <TextField {...params} label="Interest" size="small" inputProps={{ ...params.inputProps, 'aria-label': `Child ${i + 1} interest` }} />
                )}
              />
            </Grid>
            <Grid item xs={2} sm={1} sx={{ display: 'flex', justifyContent: 'center' }}>
              {row.locked ? (
                <Tooltip title="Enrolled as a student: this child stays on the enquiry">
                  <span>
                    <IconButton size="small" disabled aria-label={`Child ${i + 1} cannot be removed`}>
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <IconButton size="small" onClick={() => remove(row.key)} aria-label={`Remove child ${i + 1}`} disabled={disabled}>
                  <RemoveIcon fontSize="small" />
                </IconButton>
              )}
            </Grid>
          </Grid>
        );
      })}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => onChange([...rows, newChildRow()])}
        disabled={disabled || rows.length >= CHILDREN_MAX}
      >
        Add a child
      </Button>
    </Box>
  );
}
